import { supabase } from "../config/supabase";
import type { Tables } from "../types/supabase";
import { createSignedStorageUrl } from "./storage";

// rfq_files reads through the anon client. The backend writes the table (and
// repairs a missing file_size: services/rfqItems.repairRfqFileSize).

export type RfqFileRow = Tables<"rfq_files">;

/** The bucket the customer's Excel files are read from. */
export const RFQ_FILES_BUCKET = "rfq-files";

/** How many of an RFQ's files sit in Storage (path and bucket set and non-empty). */
export const countStoredRfqFiles = (rfqId: number) =>
  supabase
    .from("rfq_files")
    .select("*", { count: "exact", head: true })
    .eq("rfq_id", rfqId)
    .not("storage_path", "is", null)
    .not("storage_bucket", "is", null)
    .neq("storage_path", "")
    .neq("storage_bucket", "");

/** Every file row of an RFQ, newest first. */
export const listRfqFiles = (rfqId: number) =>
  supabase.from("rfq_files").select("*").eq("rfq_id", rfqId).order("created_at", { ascending: false });

/** An RFQ's Excel files that are in Storage, newest first. */
export const listRfqExcelFiles = (rfqId: number) =>
  supabase
    .from("rfq_files")
    .select("id, filename, file_path, file_size, storage_path, storage_url, created_at, updated_at")
    .eq("rfq_id", rfqId)
    .eq("file_type", "EXCEL")
    .not("storage_path", "is", null) // Only files in Supabase Storage
    .order("created_at", { ascending: false });

/**
 * Read a stored RFQ Excel back, guaranteeing the bytes currently in storage.
 *
 * The automation overwrites the workbook in place, so the pre-quote (empty)
 * version and the priced version share one storage path and one URL. An
 * ordinary read of that path can be answered from a cache populated before
 * the overwrite, which is how an empty sheet reached a customer. Every
 * read-back therefore mints a signed URL, adds a `_` parameter unique to
 * this read, and fetches it with `cache: 'no-store'` - the signed URL is
 * replayable for its lifetime, so it is those two that defeat the cache, not
 * the signing. If freshness cannot be guaranteed the read fails rather than
 * returning a copy of unknown age.
 */
export async function readStoredExcelFresh(
  rfqId: number,
  filename: string,
): Promise<{ blob: Blob } | { failure: "not_found" | "no_path" | "read_failed" }> {
  const { data: fileRecord, error: dbError } = await supabase
    .from("rfq_files")
    .select("storage_path, filename")
    .eq("rfq_id", rfqId)
    .eq("filename", filename)
    .eq("file_type", "EXCEL")
    .single();

  if (dbError || !fileRecord) {
    return { failure: "not_found" };
  }

  if (!fileRecord.storage_path) {
    return { failure: "no_path" };
  }

  const { data: signedUrlData, error: signedUrlError } = await createSignedStorageUrl(
    RFQ_FILES_BUCKET,
    fileRecord.storage_path,
    60,
  );

  if (signedUrlError || !signedUrlData?.signedUrl) {
    console.error("Supabase Storage signed URL error:", signedUrlError);
    return { failure: "read_failed" };
  }

  const signedUrl = signedUrlData.signedUrl;
  const freshUrl = `${signedUrl}${signedUrl.includes("?") ? "&" : "?"}_=${Date.now()}`;

  try {
    const response = await fetch(freshUrl, { cache: "no-store" });
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return { blob: await response.blob() };
  } catch (readError) {
    console.error("Supabase Storage download error:", readError);
    return { failure: "read_failed" };
  }
}
